/**
 * AWS Cognito Authentication Service
 *
 * Low-level wrapper around Amazon Cognito Identity SDK
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoRefreshToken
} from 'amazon-cognito-identity-js';
import { clearStoredReads } from '../utils/storedReadVersions';

import {
  REFRESH_TOKEN_KEY,
  ACCESS_TOKEN_KEY,
  ID_TOKEN_KEY,
  CognitoTokens,
  CognitoAuthResult,
  isCognitoConfigured,
  decodeJwtPayload
} from './authShared';
import {
  mockCognitoMode,
  mockCognitoAuthenticateUser,
  mockCognitoCompleteNewPasswordChallenge,
  mockCognitoForgotPassword,
  mockCognitoConfirmPassword,
  readStoredMockCognitoSession,
  isDevMockAuthEnabled,
  buildDevMockToken
} from './mockCognito';

// Configuration from environment — these must be set via VITE_ env vars (see app/.env.example)
const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;

if (!USER_POOL_ID || !CLIENT_ID) {
  console.warn('[cognitoAuth] VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID not set (OK for tests)');
}

// Lazy-load User Pool (only create when needed, not at module import time)
// This allows tests to load this module without requiring Cognito credentials
let userPool: CognitoUserPool | null = null;

function getUserPool(): CognitoUserPool {
  if (!userPool) {
    if (!USER_POOL_ID || !CLIENT_ID) {
      console.error(
        '[cognitoAuth] Missing VITE_COGNITO_USER_POOL_ID / VITE_COGNITO_CLIENT_ID — set them in app/.env',
      );
      throw new Error(
        'Sign-in is not available right now. Please contact your ZeeFleet administrator.',
      );
    }
    userPool = new CognitoUserPool({
      UserPoolId: USER_POOL_ID,
      ClientId: CLIENT_ID
    });
  }
  return userPool;
}

/**
 * Extract organization ID from JWT token claims
 * Checks for: custom:organisationId, custom:organizationId, org
 */
export function getOrganizationIdFromToken(token: string): string | null {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    console.warn('[getOrganizationIdFromToken] Failed to decode JWT payload');
    return null;
  }
  
  // Log all claims for debugging (redacted)
  const claimKeys = Object.keys(payload).filter(k => 
    k.includes('org') || k.includes('custom:') || k === 'sub' || k === 'email'
  );
  console.log('[getOrganizationIdFromToken] JWT claims found:', claimKeys);
  console.log('[getOrganizationIdFromToken] Full claim values (org-related):', {
    'custom:organisationId': payload['custom:organisationId'],
    'custom:organizationId': payload['custom:organizationId'],
    'org': payload['org'],
    'organisation': payload['organisation'],
    'organization': payload['organization'],
    'email': payload['email'],
    'cognito:username': payload['cognito:username'],
  });
  
  // Check for custom Cognito attributes (British spelling preferred)
  const orgId =
    (payload['custom:organisationId'] as string | undefined) ||
    (payload['custom:organizationId'] as string | undefined) ||
    (payload['org'] as string | undefined) ||
    (payload['organisation'] as string | undefined) ||
    (payload['organization'] as string | undefined) ||
    null;
  
  if (!orgId) {
    console.warn('[getOrganizationIdFromToken] No organization ID found in JWT token - will need to pass as header');
  } else {
    console.log('[getOrganizationIdFromToken] Organization ID found:', orgId);
  }
  
  return orgId;
}

/**
 * Authenticate user with email and password
 */
export function authenticateUser(email: string, password: string): Promise<CognitoAuthResult> {
  if (mockCognitoMode()) return mockCognitoAuthenticateUser(email, password);
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool()
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session: CognitoUserSession) => {
        const tokens: CognitoTokens = {
          accessToken: session.getAccessToken().getJwtToken(),
          idToken: session.getIdToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken()
        };

        // Log what's in the ID token for debugging
        console.log('[authenticateUser] Login successful, analyzing ID token...');
        const idTokenPayload = decodeJwtPayload(tokens.idToken);
        if (idTokenPayload) {
          console.log('[authenticateUser] ID token claims:', Object.keys(idTokenPayload));
          console.log('[authenticateUser] ID token organisationId:', idTokenPayload['custom:organisationId']);
          console.log('[authenticateUser] ID token all custom claims:', {
            'custom:organisationId': idTokenPayload['custom:organisationId'],
            'custom:organizationId': idTokenPayload['custom:organizationId'],
            'email': idTokenPayload['email'],
            'cognito:username': idTokenPayload['cognito:username'],
            'sub': idTokenPayload['sub'],
          });
        }

        // Store all tokens for fallback access
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
        localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);

        resolve({ tokens });
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: (userAttributes) => {
        // User needs to set a new password. No tokens exist yet: the public
        // CognitoAuthResult keeps `tokens` required for success-path callers,
        // so this pre-challenge result carries a null placeholder.
        resolve({
          tokens: null as unknown as CognitoTokens,
          challengeName: 'NEW_PASSWORD_REQUIRED',
          session: JSON.stringify({ userAttributes }),
          userAttributes
        });
      }
    });
  });
}

/**
 * Complete new password challenge
 */
export function completeNewPasswordChallenge(
  email: string,
  oldPassword: string,
  newPassword: string
): Promise<CognitoAuthResult> {
  if (mockCognitoMode()) return mockCognitoCompleteNewPasswordChallenge(email, newPassword);
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: oldPassword
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool()
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      newPasswordRequired: (userAttributes) => {
        // Remove attributes that can't be modified
        delete userAttributes.email_verified;
        delete userAttributes.email;

        cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
          onSuccess: (session: CognitoUserSession) => {
            const tokens: CognitoTokens = {
              accessToken: session.getAccessToken().getJwtToken(),
              idToken: session.getIdToken().getJwtToken(),
              refreshToken: session.getRefreshToken().getToken()
            };

            // Log what's in the ID token for debugging
            console.log('[completeNewPasswordChallenge] Password challenge completed, analyzing ID token...');
            const idTokenPayload = decodeJwtPayload(tokens.idToken);
            if (idTokenPayload) {
              console.log('[completeNewPasswordChallenge] ID token claims:', Object.keys(idTokenPayload));
              console.log('[completeNewPasswordChallenge] ID token organisationId:', idTokenPayload['custom:organisationId']);
            }

            // Store refresh token
            localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
            localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
            localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);

            resolve({ tokens });
          },
          onFailure: (err) => {
            reject(err);
          }
        });
      },
      onSuccess: () => {
        reject(new Error('Expected NEW_PASSWORD_REQUIRED challenge'));
      },
      onFailure: (err) => {
        reject(err);
      }
    });
  });
}

/**
 * Refresh session using refresh token
 */
export function refreshSession(): Promise<CognitoTokens | null> {
  // Mock-Cognito mode: the stored tokens are far-future unsigned JWTs minted
  // by the mock — read them, no round-trip (this also bypasses the user-pool
  // factory that would throw in a no-Cognito dev browser).
  if (mockCognitoMode()) {
    console.log('[refreshSession] 🔧 Mock-Cognito mode: reading stored tokens');
    return Promise.resolve(readStoredMockCognitoSession());
  }

  // Dev-gated short-circuit: in a no-Cognito dev browser, mint a fixed dev
  // token so the app's authenticated-fetch path reaches the mock backend
  // with a valid Authorization header. Inert in production and when
  // Cognito is configured.
  if (isDevMockAuthEnabled(import.meta.env.DEV, isCognitoConfigured())) {
    console.log('[refreshSession] 🔧 Cognito unconfigured in development: using fixed dev token');
    const token = buildDevMockToken();
    const tokens: CognitoTokens = {
      accessToken: token,
      idToken: token,
      refreshToken: token,
    };
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);
    return Promise.resolve(tokens);
  }

  return new Promise((resolve) => {
    console.log('[refreshSession] 🔄 Starting session refresh...');
    
    // First, try to get current user from Cognito
    const currentUser = getUserPool().getCurrentUser();
    
    if (!currentUser) {
      console.warn('[refreshSession] ⚠️  No current user in Cognito pool, checking localStorage fallback...');
      
      // Fallback: Check if we have stored tokens
      const storedAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      const storedIdToken = localStorage.getItem(ID_TOKEN_KEY);
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      
      console.log('[refreshSession] 📦 Stored tokens found:', {
        hasAccessToken: !!storedAccessToken,
        hasIdToken: !!storedIdToken,
        hasRefreshToken: !!storedRefreshToken,
      });
      
      if (storedAccessToken && storedIdToken && storedRefreshToken) {
        // Check if tokens are still valid (basic check - not expired)
        try {
          const accessTokenPayload = JSON.parse(atob(storedAccessToken.split('.')[1]));
          const exp = accessTokenPayload.exp;
          const now = Math.floor(Date.now() / 1000);
          
          console.log('[refreshSession] ⏱️  Token expiration check:', {
            expiresIn: exp - now,
            isExpired: exp < now,
            expiresIn60s: exp < now + 60,
          });
          
          // If token is expired or expires in less than 1 minute, return null
          if (exp && exp < now + 60) {
            console.warn('[refreshSession] ⏰ Stored access token expired or expiring soon, need to refresh');
            // Try to refresh using stored refresh token
            // But we need the username to create a CognitoUser
            const idTokenPayload = JSON.parse(atob(storedIdToken.split('.')[1]));
            const username = idTokenPayload.email || idTokenPayload['cognito:username'];
            
            console.log('[refreshSession] 🔑 Extracted username for refresh:', username);
            
            if (username && storedRefreshToken) {
              const cognitoUser = new CognitoUser({
                Username: username,
                Pool: getUserPool()
              });
              
              cognitoUser.refreshSession(new CognitoRefreshToken({ RefreshToken: storedRefreshToken }), (refreshErr, refreshedSession) => {
                if (refreshErr || !refreshedSession) {
                  console.error('[refreshSession] ❌ Failed to refresh using stored refresh token:', refreshErr);
                  resolve(null);
                  return;
                }
                
                const tokens: CognitoTokens = {
                  accessToken: refreshedSession.getAccessToken().getJwtToken(),
                  idToken: refreshedSession.getIdToken().getJwtToken(),
                  refreshToken: refreshedSession.getRefreshToken().getToken()
                };
                
                console.log('[refreshSession] ✅ Successfully refreshed tokens');
                
                // Update stored tokens
                localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
                localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
                localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);
                
                resolve(tokens);
              });
              return;
            }
          } else {
            // Token is still valid, use stored tokens
            console.log('[refreshSession] ✅ Stored tokens are still valid (fallback)');
            resolve({
              accessToken: storedAccessToken,
              idToken: storedIdToken,
              refreshToken: storedRefreshToken
            });
            return;
          }
        } catch (e) {
          console.error('[refreshSession] ❌ Error parsing stored tokens:', e);
        }
      }
      
      console.error('[refreshSession] ❌ No current user and no valid stored tokens');
      resolve(null);
      return;
    }

    console.log('[refreshSession] ✅ Found current user in Cognito pool');
    
    currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err) {
        console.error('[refreshSession] ❌ Session refresh failed:', err);
        // Try to refresh the session using refresh token
        const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (storedRefreshToken) {
          console.log('[refreshSession] 🔄 Attempting to refresh session using stored refresh token...');
          currentUser.refreshSession(new CognitoRefreshToken({ RefreshToken: storedRefreshToken }), (refreshErr, refreshedSession) => {
            if (refreshErr || !refreshedSession) {
              console.error('[refreshSession] Failed to refresh session:', refreshErr);
              resolve(null);
              return;
            }
            
            const tokens: CognitoTokens = {
              accessToken: refreshedSession.getAccessToken().getJwtToken(),
              idToken: refreshedSession.getIdToken().getJwtToken(),
              refreshToken: refreshedSession.getRefreshToken().getToken()
            };
            
            console.log('[refreshSession] ✅ Successfully refreshed session');
            
            // Update stored tokens
            localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
            localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
            localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);
            resolve(tokens);
          });
        } else {
          console.error('[refreshSession] ❌ No stored refresh token available');
          resolve(null);
        }
        return;
      }

      if (!session || !session.isValid()) {
        console.warn('[refreshSession] ⚠️  Session is invalid or expired');
        resolve(null);
        return;
      }

      console.log('[refreshSession] ✅ Session is valid');

      const tokens: CognitoTokens = {
        accessToken: session.getAccessToken().getJwtToken(),
        idToken: session.getIdToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken()
      };

      // Update stored tokens
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);
      
      console.log('[refreshSession] ✅ Returning tokens to caller');
      resolve(tokens);
    });
  });
}

/**
 * Forgot password - initiate password reset
 */
export function forgotPassword(email: string): Promise<void> {
  if (mockCognitoMode()) return mockCognitoForgotPassword(email);
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool()
    });

    cognitoUser.forgotPassword({
      onSuccess: () => {
        resolve();
      },
      onFailure: (err) => {
        reject(err);
      }
    });
  });
}

/**
 * Confirm password reset with verification code
 */
export function confirmPassword(email: string, verificationCode: string, newPassword: string): Promise<void> {
  if (mockCognitoMode()) return mockCognitoConfirmPassword(email, verificationCode, newPassword);
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool()
    });

    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: () => {
        resolve();
      },
      onFailure: (err) => {
        reject(err);
      }
    });
  });
}

/**
 * Sign out user
 */
export function signOut(): void {
  if (USER_POOL_ID && CLIENT_ID) {
    const currentUser = getUserPool().getCurrentUser();

    if (currentUser) {
      currentUser.signOut();
    }
  }

  clearStoredReads();
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem('zecure_current_user');
  localStorage.removeItem('zecure_user_info');
  localStorage.removeItem('zecure_session_token');
}

/**
 * Check if user has a stored session
 */
export function hasStoredSession(): boolean {
  // No user pool to query when Cognito is unconfigured (mock-Cognito mode,
  // no-auth dev, tests): the stored refresh token IS the session. Same result
  // as the try/catch below (the pool lookup throws and is caught), without
  // the pool factory's console.error on every fresh load.
  if (!isCognitoConfigured()) {
    return !!localStorage.getItem(REFRESH_TOKEN_KEY);
  }
  try {
    return !!localStorage.getItem(REFRESH_TOKEN_KEY) || !!getUserPool().getCurrentUser();
  } catch {
    // If Cognito not configured (tests), just check localStorage
    return !!localStorage.getItem(REFRESH_TOKEN_KEY);
  }
}

/**
 * Get current user email from token
 */
export function getCurrentUserEmail(): Promise<string | null> {
  if (!USER_POOL_ID || !CLIENT_ID) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const currentUser = getUserPool().getCurrentUser();
    
    if (!currentUser) {
      resolve(null);
      return;
    }

    currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        resolve(null);
        return;
      }

      const email = session.getIdToken().payload.email;
      resolve(email || null);
    });
  });
}

/**
 * Sync read of login email from the stored ID token (for stamping `updated_by`).
 * Prefer this for save payloads; falls back to getCurrentUserEmail() when absent.
 */
export function readSessionUserEmail(): string | null {
  try {
    const idToken = localStorage.getItem(ID_TOKEN_KEY);
    if (!idToken) return null;
    const payload = decodeJwtPayload(idToken);
    if (!payload) return null;
    const email = payload.email;
    if (typeof email === 'string' && email.trim()) return email.trim();
    const username = payload['cognito:username'];
    if (typeof username === 'string' && username.includes('@')) return username.trim();
    return null;
  } catch {
    return null;
  }
}

export default {
  authenticateUser,
  completeNewPasswordChallenge,
  refreshSession,
  forgotPassword,
  confirmPassword,
  signOut,
  hasStoredSession,
  getCurrentUserEmail,
  readSessionUserEmail,
};
